import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { 
  CheckCircle, 
  XCircle, 
  Flag, 
  Clock, 
  User, 
  MessageSquare,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useApprovalHistoryQuery } from '@/hooks/useApprovalHistory';
import type { ApprovalHistoryWithUsers } from '@/types/gallery';

interface ApprovalHistoryProps {
  entityType: 'gallery' | 'image';
  entityId: number;
  title?: string;
  showEntityDetails?: boolean;
  limit?: number;
  className?: string;
}

const actionIcons = {
  approved: CheckCircle,
  rejected: XCircle,
  flagged: Flag,
  under_review: Clock
} as const;

const actionColors = {
  approved: 'text-green-600 bg-green-50',
  rejected: 'text-red-600 bg-red-50',
  flagged: 'text-yellow-600 bg-yellow-50',
  under_review: 'text-blue-600 bg-blue-50'
} as const;

const actionLabels = {
  approved: 'Approved',
  rejected: 'Rejected',
  flagged: 'Flagged',
  under_review: 'Under Review'
} as const;

export const ApprovalHistory: React.FC<ApprovalHistoryProps> = ({
  entityType,
  entityId,
  title,
  showEntityDetails = false,
  limit = 10,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  
  const { data: history = [], isLoading, error } = useApprovalHistoryQuery({
    entity_type: entityType,
    entity_id: entityId,
    limit
  });

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="text-red-600 text-center py-8">
            Failed to load approval history
          </div>
        </CardContent>
      </Card>
    );
  }

  if (history.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="text-gray-500 text-center py-8">
            No approval history found
          </div>
        </CardContent>
      </Card>
    );
  }

  const displayedHistory = isExpanded ? history : history.slice(0, 3);

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Clock className="h-5 w-5" />
          {title || 'Approval History'}
          <Badge variant="secondary" className="ml-auto">
            {history.length} {history.length === 1 ? 'action' : 'actions'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-4">
          {displayedHistory.map((entry, index) => (
            <ApprovalHistoryEntry 
              key={entry.id} 
              entry={entry} 
              showEntityDetails={showEntityDetails}
              isLatest={index === 0}
            />
          ))}
          
          {history.length > 3 && (
            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full">
                  {isExpanded ? (
                    <>
                      <ChevronUp className="h-4 w-4 mr-2" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 mr-2" />
                      Show {history.length - 3} More Actions
                    </>
                  )}
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

interface ApprovalHistoryEntryProps {
  entry: ApprovalHistoryWithUsers;
  showEntityDetails?: boolean;
  isLatest?: boolean;
}

const ApprovalHistoryEntry: React.FC<ApprovalHistoryEntryProps> = ({
  entry,
  showEntityDetails = false,
  isLatest = false
}) => {
  const ActionIcon = actionIcons[entry.action as keyof typeof actionIcons] || Clock;
  const actionColor = actionColors[entry.action as keyof typeof actionColors] || 'text-gray-600 bg-gray-50';
  const actionLabel = actionLabels[entry.action as keyof typeof actionLabels] || entry.action;

  return (
    <div className={`relative pl-8 pb-4 ${!isLatest ? 'border-l-2 border-gray-200' : ''}`}>
      {/* Timeline dot */}
      <div className={`absolute left-0 top-0 -ml-2 w-4 h-4 rounded-full ${actionColor} flex items-center justify-center`}>
        <ActionIcon className="h-3 w-3" />
      </div>
      
      <div className="space-y-2">
        {/* Action header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className={actionColor}>
              {actionLabel}
            </Badge>
            {entry.previous_status && (
              <span className="text-sm text-gray-500">
                from {entry.previous_status}
              </span>
            )}
          </div>
          <time className="text-sm text-gray-500" title={new Date(entry.created_at).toLocaleString()}>
            {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
          </time>
        </div>

        {/* Approver info */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <User className="h-4 w-4" />
          <span>
            {entry.approver_name || entry.approver_email || 'Unknown User'}
            {entry.approver_role && (
              <Badge variant="outline" className="ml-2 text-xs">
                {entry.approver_role}
              </Badge>
            )}
          </span>
        </div>

        {/* Entity details */}
        {showEntityDetails && (
          <div className="text-sm text-gray-600">
            {entry.entity_type === 'gallery' ? (
              <div>
                Gallery: {entry.gallery_title} ({entry.gallery_franchise_region})
              </div>
            ) : (
              <div>
                Image: {entry.image_filename} 
                {entry.image_job_number && ` (Job #${entry.image_job_number})`}
                {entry.image_franchise_region && ` - ${entry.image_franchise_region}`}
              </div>
            )}
          </div>
        )}

        {/* Reason/Notes */}
        {(entry.reason || entry.notes) && (
          <div className="bg-gray-50 rounded-md p-3 text-sm">
            <div className="flex items-start gap-2">
              <MessageSquare className="h-4 w-4 mt-0.5 text-gray-500" />
              <div>
                {entry.reason && (
                  <div className="text-gray-700 mb-1">
                    <strong>Reason:</strong> {entry.reason}
                  </div>
                )}
                {entry.notes && (
                  <div className="text-gray-600">
                    <strong>Notes:</strong> {entry.notes}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalHistory;